import { useState, useMemo, useRef, useCallback } from "react";
import { Filter, ChevronDown, ChevronRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { useIsMobile } from "@/hooks/use-mobile";
import { usePermissions } from "@/hooks/usePermissions";
import { GlobalSearchFilters, SearchSectionId, SEARCH_SECTIONS } from "@/hooks/useGlobalSearch";
import { cn } from "@/lib/utils";

interface GlobalSearchFilterPopoverProps {
  filters: GlobalSearchFilters;
  onFiltersChange: (filters: GlobalSearchFilters) => void;
}

// Hierarchical section structure
const SECTION_HIERARCHY = [
  {
    id: 'crm',
    label: 'CRM',
    children: [
      { id: 'kunden' as SearchSectionId, label: 'Kunden' },
      { id: 'eigenvertraege' as SearchSectionId, label: 'Eigenverträge' },
      { id: 'fremdvertraege' as SearchSectionId, label: 'Fremdverträge' },
    ]
  },
  {
    id: 'produktanbieter',
    label: 'Produktanbieter',
    children: [
      { id: 'gesellschaften' as SearchSectionId, label: 'Gesellschaften' },
      { id: 'ansprechpartner' as SearchSectionId, label: 'Ansprechpartner' },
      { id: 'zugaenge' as SearchSectionId, label: 'Zugänge' },
      { id: 'vermittlernummern' as SearchSectionId, label: 'Vermittlernummern' },
      { id: 'rabatte' as SearchSectionId, label: 'Rabatte' },
    ]
  },
  {
    id: 'leads_group',
    label: 'Leads',
    children: [
      { id: 'leads' as SearchSectionId, label: 'Leads' },
    ]
  },
  {
    id: 'aufgaben_group',
    label: 'Aufgaben',
    children: [
      { id: 'aufgaben' as SearchSectionId, label: 'Aufgaben' },
    ]
  },
  {
    id: 'vertriebspartner_group',
    label: 'Vertriebspartner',
    children: [
      { id: 'vertriebspartner' as SearchSectionId, label: 'Vertriebspartner' },
    ]
  },
  {
    id: 'buchhaltung_group',
    label: 'Buchhaltung',
    children: [
      { id: 'abrechnungen' as SearchSectionId, label: 'Abrechnungen' },
      { id: 'buchhaltung' as SearchSectionId, label: 'Belege' },
      { id: 'vorlagen' as SearchSectionId, label: 'Vorlagen' },
    ]
  },
  {
    id: 'content_group',
    label: 'Inhalte',
    children: [
      { id: 'downloadcenter' as SearchSectionId, label: 'Downloadcenter' },
      { id: 'elearning' as SearchSectionId, label: 'E-Learning' },
    ]
  },
  {
    id: 'other',
    label: 'Sonstige',
    children: [
      { id: 'berichte' as SearchSectionId, label: 'Berichte' },
      { id: 'einstellungen' as SearchSectionId, label: 'Einstellungen' },
    ]
  },
];

const ALL_SECTION_IDS: SearchSectionId[] = SECTION_HIERARCHY.flatMap(g => g.children.map(c => c.id));

export function GlobalSearchFilterPopover({ filters, onFiltersChange }: GlobalSearchFilterPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['crm', 'produktanbieter']));
  const isMobile = useIsMobile();
  const { canAccess } = usePermissions();
  
  // Scroll position preservation
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<number>(0);

  const saveScrollPosition = useCallback(() => {
    if (scrollRef.current) {
      const viewport = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (viewport) {
        scrollPositionRef.current = viewport.scrollTop;
      }
    }
  }, []);

  const restoreScrollPosition = useCallback(() => {
    requestAnimationFrame(() => {
      if (scrollRef.current) {
        const viewport = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
        if (viewport) {
          viewport.scrollTop = scrollPositionRef.current;
        }
      }
    });
  }, []);

  const handleFiltersChange = useCallback((newFilters: GlobalSearchFilters) => {
    saveScrollPosition();
    onFiltersChange(newFilters);
    restoreScrollPosition();
  }, [onFiltersChange, saveScrollPosition, restoreScrollPosition]);

  // Filter sections based on user permissions
  const visibleSections = useMemo(() => {
    return SECTION_HIERARCHY.map(group => ({
      ...group,
      children: group.children.filter(child => {
        const section = SEARCH_SECTIONS[child.id];
        return section && canAccess(section.module);
      })
    })).filter(group => group.children.length > 0);
  }, [canAccess]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    const allVisibleIds = visibleSections.flatMap(g => g.children.map(c => c.id));
    if (filters.enabledSections.size < allVisibleIds.length) count++;
    if (filters.kundentyp && filters.kundentyp !== 'all') count++;
    if (filters.leadheldenLead && filters.leadheldenLead !== 'all') count++;
    if (filters.exactMatch) count++;
    if (filters.fuzzyMatch) count++;
    return count;
  }, [filters, visibleSections]);

  const toggleSection = (sectionId: SearchSectionId, checked: boolean) => {
    const newEnabled = new Set(filters.enabledSections);
    if (checked) {
      newEnabled.add(sectionId);
    } else {
      newEnabled.delete(sectionId);
    }
    handleFiltersChange({ ...filters, enabledSections: newEnabled });
  };

  const toggleGroup = (groupId: string) => {
    saveScrollPosition();
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
    restoreScrollPosition();
  };

  const toggleAllChildren = (children: { id: SearchSectionId }[], checked: boolean) => {
    const newEnabled = new Set(filters.enabledSections);
    children.forEach(child => {
      if (checked) {
        newEnabled.add(child.id);
      } else {
        newEnabled.delete(child.id);
      }
    });
    handleFiltersChange({ ...filters, enabledSections: newEnabled });
  };

  const selectAll = () => {
    const allVisibleIds = visibleSections.flatMap(g => g.children.map(c => c.id));
    handleFiltersChange({ ...filters, enabledSections: new Set(allVisibleIds) });
  };

  const clearAll = () => {
    handleFiltersChange({ ...filters, enabledSections: new Set() });
  };

  const resetFilters = () => {
    const allVisibleIds = visibleSections.flatMap(g => g.children.map(c => c.id));
    handleFiltersChange({
      enabledSections: new Set(allVisibleIds),
      kundentyp: 'all',
      leadheldenLead: 'all',
      partnerId: undefined,
      exactMatch: false,
      fuzzyMatch: false,
    });
  };

  const FilterContent = () => (
    <div className="flex flex-col h-full">
      {/* Fixed Header */}
      <div className="flex flex-col gap-2 p-4 pb-3 border-b flex-shrink-0">
        <div className="flex items-center justify-between">
          <span className="font-medium text-sm">Suchfilter</span>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={selectAll} className="h-7 text-xs px-2">
              Alle
            </Button>
            <Button variant="ghost" size="sm" onClick={clearAll} className="h-7 text-xs px-2">
              Keine
            </Button>
          </div>
        </div>
        {/* Search Mode Toggles */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <Switch
              id="fuzzy-toggle"
              checked={filters.fuzzyMatch || false}
              onCheckedChange={(checked) => handleFiltersChange({ ...filters, fuzzyMatch: checked, exactMatch: checked ? false : filters.exactMatch })}
              className="scale-75 origin-left"
            />
            <label htmlFor="fuzzy-toggle" className="text-xs text-muted-foreground cursor-pointer">Ungefähr</label>
          </div>
          <div className="flex items-center gap-1.5">
            <Switch
              id="exact-toggle"
              checked={filters.exactMatch || false}
              onCheckedChange={(checked) => handleFiltersChange({ ...filters, exactMatch: checked, fuzzyMatch: checked ? false : filters.fuzzyMatch })}
              className="scale-75 origin-left"
            />
            <label htmlFor="exact-toggle" className="text-xs text-muted-foreground cursor-pointer">Exakt</label>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <ScrollArea ref={scrollRef} className="flex-1 min-h-0">
        <div className="p-4 pt-3 space-y-4">
          {/* Search Sections */}
          <div className="space-y-1">
              {visibleSections.map(group => {
                const isExpanded = expandedGroups.has(group.id);
                const allChecked = group.children.every(c => filters.enabledSections.has(c.id));
                const someChecked = group.children.some(c => filters.enabledSections.has(c.id));
                
                return (
                  <Collapsible key={group.id} open={isExpanded} onOpenChange={() => toggleGroup(group.id)}>
                    <div className="flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-accent/50">
                      <Checkbox
                        id={`group-${group.id}`}
                        checked={allChecked}
                        ref={(el) => {
                          if (el) {
                            (el as any).indeterminate = someChecked && !allChecked;
                          }
                        }}
                        onCheckedChange={(checked) => toggleAllChildren(group.children, !!checked)}
                        className="h-4 w-4"
                      />
                      <CollapsibleTrigger asChild>
                        <button className="flex items-center gap-1 flex-1 text-left text-sm font-medium">
                          {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                          {group.label}
                          <span className="text-xs text-muted-foreground ml-1">
                            ({group.children.filter(c => filters.enabledSections.has(c.id)).length}/{group.children.length})
                          </span>
                        </button>
                      </CollapsibleTrigger>
                    </div>
                    <CollapsibleContent>
                      <div className="ml-6 space-y-0.5 py-1">
                        {group.children.map(child => (
                          <label
                            key={child.id}
                            className="flex items-center gap-2 py-1 px-2 rounded-md hover:bg-accent/50 cursor-pointer"
                          >
                            <Checkbox
                              checked={filters.enabledSections.has(child.id)}
                              onCheckedChange={(checked) => toggleSection(child.id, !!checked)}
                              className="h-3.5 w-3.5"
                            />
                            <span className="text-sm">{child.label}</span>
                          </label>
                        ))}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                );
              })}
          </div>

          {/* Additional Filters */}
          <div className="space-y-3 pt-2 border-t">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Zusätzliche Filter</span>
            
            {/* Kundentyp */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Kundentyp</label>
              <Select
                value={filters.kundentyp || 'all'}
                onValueChange={(value) => handleFiltersChange({ ...filters, kundentyp: value as any })}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle</SelectItem>
                  <SelectItem value="privat">Privat</SelectItem>
                  <SelectItem value="gewerbe">Gewerbe</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Leadhelden Lead */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Leadhelden Lead</label>
              <Select
                value={filters.leadheldenLead || 'all'}
                onValueChange={(value) => handleFiltersChange({ ...filters, leadheldenLead: value as any })}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle</SelectItem>
                  <SelectItem value="ja">Ja</SelectItem>
                  <SelectItem value="nein">Nein</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </ScrollArea>

      {/* Fixed Footer - Mobile only */}
      {isMobile && (
        <div className="flex gap-2 p-4 border-t flex-shrink-0">
          <Button variant="outline" size="sm" onClick={resetFilters} className="flex-1">
            Zurücksetzen
          </Button>
          <Button size="sm" onClick={() => setIsOpen(false)} className="flex-1">
            Anwenden
          </Button>
        </div>
      )}
    </div>
  );

  const TriggerButton = (
    <Button
      variant="ghost"
      size="icon"
      className={cn(
        "h-9 w-9 relative",
        activeFilterCount > 0 && "text-primary"
      )}
    >
      <Filter className="h-4 w-4" />
      {activeFilterCount > 0 && (
        <Badge
          variant="default"
          className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-[10px]"
        >
          {activeFilterCount}
        </Badge>
      )}
    </Button>
  );

  if (isMobile) {
    return (
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          {TriggerButton}
        </SheetTrigger>
        <SheetContent side="bottom" className="h-[85vh] p-0">
          <SheetHeader className="sr-only">
            <SheetTitle>Suchfilter</SheetTitle>
          </SheetHeader>
          <FilterContent />
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        {TriggerButton}
      </PopoverTrigger>
      <PopoverContent 
        className="w-[320px] p-0 h-[80vh] max-h-[80vh] overflow-hidden" 
        align="end"
        sideOffset={8}
      >
        <FilterContent />
      </PopoverContent>
    </Popover>
  );
}
